import { useEffect, useRef, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { DARK_MAP_STYLES, useIsDarkClass } from "@/lib/map-styles";

interface WholesalerListingMapProps {
  address: string;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export default function WholesalerListingMap({ address, city, state, zipCode, latitude, longitude }: WholesalerListingMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [geocodeFailed, setGeocodeFailed] = useState(false);
  const storedCoords = (typeof latitude === "number" && typeof longitude === "number")
    ? { lat: latitude, lng: longitude }
    : null;
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(storedCoords);

  const { data: mapsKeyData, isLoading: keyLoading } = useQuery<{ key: string }>({
    queryKey: ["/api/app/maps-key"],
  });
  const apiKey = mapsKeyData?.key || "";
  const isDark = useIsDarkClass();

  const fullAddress = [address, city, state, zipCode].filter(Boolean).join(", ");

  const loadGoogleMaps = useCallback(() => {
    if (!apiKey) return;
    if (window.google?.maps?.Map) {
      setMapLoaded(true);
      return;
    }
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      const check = setInterval(() => {
        if (window.google?.maps?.Map) {
          clearInterval(check);
          setMapLoaded(true);
        }
      }, 100);
      return;
    }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const check = setInterval(() => {
        if (window.google?.maps?.Map) {
          clearInterval(check);
          setMapLoaded(true);
        }
      }, 50);
    };
    script.onerror = () => setMapError("Failed to load Google Maps");
    document.head.appendChild(script);
  }, [apiKey]);

  useEffect(() => {
    loadGoogleMaps();
  }, [loadGoogleMaps]);

  useEffect(() => {
    if (!mapLoaded) return;
    // Prefer the server-cached coords; only fall back to client-side geocoding
    // when the listing record has no stored latitude/longitude (Task #1555).
    if (storedCoords) {
      setGeocodeFailed(false);
      setCoords(storedCoords);
      return;
    }
    if (!fullAddress || !address.trim()) {
      setGeocodeFailed(true);
      return;
    }
    let cancelled = false;
    setGeocodeFailed(false);
    setCoords(null);
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: fullAddress }, (results, status) => {
      if (cancelled) return;
      if (status === "OK" && results && results[0]) {
        const loc = results[0].geometry.location;
        setCoords({ lat: loc.lat(), lng: loc.lng() });
      } else {
        setGeocodeFailed(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [mapLoaded, fullAddress, address, latitude, longitude]);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !coords) return;

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new google.maps.Map(mapRef.current, {
        zoom: 15,
        center: coords,
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: true,
        fullscreenControl: true,
        styles: isDark ? DARK_MAP_STYLES : [],
      });
    } else {
      mapInstanceRef.current.setCenter(coords);
      mapInstanceRef.current.setZoom(15);
    }

    if (markerRef.current) {
      markerRef.current.setMap(null);
    }
    markerRef.current = new google.maps.Marker({
      map: mapInstanceRef.current,
      position: coords,
      title: address,
      icon: "https://maps.google.com/mapfiles/ms/icons/orange-dot.png",
    });
  }, [mapLoaded, coords, address, isDark]);

  useEffect(() => {
    if (!mapInstanceRef.current) return;
    mapInstanceRef.current.setOptions({ styles: isDark ? DARK_MAP_STYLES : [] });
  }, [isDark]);

  if (!apiKey && !keyLoading) return null;
  if (mapError || geocodeFailed) return null;

  if (keyLoading || !mapLoaded || !coords) {
    return <Skeleton className="h-[320px] w-full rounded-xl" data-testid="wholesaler-map-loading" />;
  }

  return (
    <div className="mb-6" data-testid="wholesaler-listing-map-container">
      <div
        ref={mapRef}
        className="w-full h-[320px] rounded-xl border shadow-sm"
        data-testid="wholesaler-listing-map"
      />
    </div>
  );
}
